import { HttpErrorResponse } from '@angular/common/http';
import {
  AuthTokenRES,
  BffUserRES,
  BusinessUnitRES,
  FactorRES,
  FactorsRES,
  FeatureFlagRES,
  FirmRES,
  GetFeatureFlagRES,
  GetTenantRES,
  Masquerading,
  SubscriptionRES
} from '@smarsh-cyber/shared/api';
import {
  AppInit,
  AuthToken,
  BusinessUnit,
  DEFAULT_REQ_STATE,
  Factor,
  FactorsModel,
  FeaturePermissions,
  ReqStatus,
  UserDetail,
  UserPermissionsModel
} from '@smarsh-cyber/shared/models';
import { getErrorDetails } from '@smarsh-cyber/shared/utils';
import { SORT_ORDER } from '@smarsh-cyber/shared/constants';
import { sortArrayByAlphabetically } from '@smarsh-cyber/shared/utils';

export const translateGetAppInitData = (
  token: AuthTokenRES,
  orgList: FirmRES,
  tenantDetails: GetTenantRES,
  bffUserDetails: BffUserRES,
  permissions: FeatureFlagRES[],
  subscriptions: { sat: SubscriptionRES; phishing: SubscriptionRES },
  masquerading: Masquerading
): AppInit => {
  const orgListRES = orgList?.data?.response;
  const orgListData: BusinessUnit[] = sortArrayByAlphabetically(
    orgListRES?.map((org: BusinessUnitRES) => {
      return {
        id: org.businessUnitId,
        name: org.businessUnitName,
        applianceId: +org.applianceId
      };
    }),
    'name',
    SORT_ORDER.ASC
  );

  const userDetails = bffUserDetails || null;
  const user = translateUser(
    userDetails,
    permissions,
    subscriptions,
    masquerading
  );

  return {
    businessUnits: orgListData,
    user,
    userToken: {
      tenantId: tenantDetails?.data?.tenantId ?? '',
      authToken: {
        accessToken: token?.accessToken,
        refreshToken: token?.refreshToken
      },
      firmId: tenantDetails?.data?.firmId
    },
    loggedInUser: null
  };
};

export const translatePermissions = (
  permissions: FeatureFlagRES[],
  subscriptions: { sat: SubscriptionRES; phishing: SubscriptionRES }
): FeaturePermissions => {
  //TODO: temporary fix for the permissions, remove after feature flag implementation
  const featurePermissions = [];
  Object.keys(subscriptions).forEach((key) => {
    if (subscriptions[key]?.serviceStatus === 'TRUE') {
      featurePermissions.push(`${key.toLowerCase()}:read`);
    }
  });
  return featurePermissions as FeaturePermissions;
  // end of temporary fix

  // return permissions?.flatMap(({ featureName, apiAccess }) =>
  //   apiAccess.map((access) => `${featureName}:${access}`)
  // ) as FeaturePermissions;
};

export const translateRefreshToken = (tokenRES: AuthTokenRES): AuthToken => {
  return {
    accessToken: tokenRES?.accessToken ?? '',
    refreshToken: tokenRES?.refreshToken ?? ''
  };
};

export const translateFactors = (res: FactorsRES): FactorsModel => {
  const factors: Factor[] = [];
  res?.data?.forEach((factor: FactorRES) => {
    if (factor.factorName)
      factors.push({
        id: factor.id,
        factorName: factor.factorName,
        isEnable: factor.isEnable,
        isException: factor.isException,
        isRemediation: factor.isRemediation,
        key: factor.key
      });
  });

  return {
    factors: factors,
    loaded: true
  };
};

export const translateUser = (
  userDetails: BffUserRES,
  permissions: FeatureFlagRES[],
  subscriptions: { sat: SubscriptionRES; phishing: SubscriptionRES },
  masquerading?: Masquerading
): UserDetail => {
  return {
    permissions: translatePermissions(permissions, subscriptions),
    userId: userDetails?.userId ?? '',
    businessUnitId:
      userDetails?.buId && userDetails?.buId !== 'null'
        ? userDetails?.buId
        : '',
    role: userDetails?.role,
    businessUnitName: userDetails?.buName,
    username: userDetails?.username ?? '',
    position_id: Number(userDetails?.positionId),
    profile: {
      email: userDetails?.email ?? '',
      fname: userDetails?.firstName ?? '',
      lname: userDetails?.lastName ?? '',
      phone: userDetails?.phoneNumber ?? ''
    },
    status: userDetails.status,
    masquerading: masquerading ? masquerading : null
  };
};

export const translateUserPermission = (
  response: GetFeatureFlagRES | HttpErrorResponse
): UserPermissionsModel => {
  const { isHttpError, errorData } = getErrorDetails(response);

  const message =
    isHttpError && errorData?.error?.[0]?.errorDescription
      ? errorData.error[0].errorDescription
      : response?.message;

  return {
    errors: isHttpError ? [{ message: errorData?.error?.message }] : [],
    data: isHttpError ? null : (response as GetFeatureFlagRES)?.data,
    pageState: null,
    reqState: {
      ...DEFAULT_REQ_STATE,
      message,
      reqStatus: isHttpError ? ReqStatus.ERROR : ReqStatus.SUCCESS
    }
  };
};
